<template>
	<div class="portal-page market-page">
		<section class="portal-section portal-section--compact">
			<div class="portal-section__head">
				<h1>员工市场</h1>
				<p>围绕财务、HR、法务与数据岗位，把数字员工按工作结果组织成可直接启用的目录。</p>
			</div>
			<div class="market-filters">
				<button
					v-for="item in filters"
					:key="item.key"
					:class="{ active: activeCategory === item.key }"
					@click="switchCategory(item.key)"
				>
					{{ item.label }}
				</button>
			</div>
			<div class="market-lanes">
				<button
					v-for="lane in marketLanes"
					:key="lane.key"
					:class="['market-lane', { active: activeCategory === lane.key }]"
					@click="switchCategory(lane.key)"
				>
					<span>{{ lane.label }}</span>
					<strong>{{ lane.headline }}</strong>
					<small>{{ lane.note }}</small>
				</button>
			</div>
		</section>

		<section class="market-grid">
			<article class="market-card" v-for="agent in filteredAgents" :key="agent.title">
				<div class="market-card__head">
					<div class="market-card__lead">
						<div class="market-card__icon"><i :class="agent.icon"></i></div>
						<div class="market-card__title">
							<div class="market-card__eyebrow">{{ agent.track }}</div>
							<h3>{{ agent.title }}</h3>
						</div>
					</div>
					<div class="market-card__badge">{{ agent.badge }}</div>
				</div>
				<p>{{ agent.desc }}</p>
				<div class="market-card__meta">
					<div class="market-card__meta-item" v-for="item in agent.meta" :key="item.label">
						<span>{{ item.label }}</span>
						<strong>{{ item.value }}</strong>
					</div>
				</div>
				<div class="market-card__tags">
					<span v-for="tag in agent.tags" :key="tag">{{ tag }}</span>
				</div>
				<div class="market-card__footer">
					<Button color="primary" size="s">启用岗位</Button>
					<a @click.prevent="$router.push({ name: 'Office' })">进入办公室</a>
				</div>
			</article>
		</section>
	</div>
</template>

<script>
const LEGACY_CATEGORY_MAP = {
	tax: 'finance',
	ops: 'hr',
	automation: 'data'
};

const VALID_CATEGORIES = ['all', 'finance', 'hr', 'legal', 'data'];

const normalizeCategory = value => {
	if (!value) {
		return 'all';
	}
	const nextValue = LEGACY_CATEGORY_MAP[value] || value;
	return VALID_CATEGORIES.includes(nextValue) ? nextValue : 'all';
};

export default {
	name: 'EmployeeMarket',
	data() {
		return {
			activeCategory: normalizeCategory(this.$route.query.category),
			filters: [
				{ key: 'all', label: '全部' },
				{ key: 'finance', label: '财务数字员工' },
				{ key: 'hr', label: 'HR数字员工' },
				{ key: 'legal', label: '法务数字员工' },
				{ key: 'data', label: '数据专员' }
			],
			marketLanes: [
				{ key: 'finance', label: '财务数字员工', headline: '结账、资金、预算与利润同步跟进', note: '适合财务负责人、出纳、经营分析团队' },
				{ key: 'hr', label: 'HR数字员工', headline: '招聘、入转调离与组织节奏协同', note: '适合人事专员、HRBP、行政协同岗' },
				{ key: 'legal', label: '法务数字员工', headline: '合同预审、台账追踪与合规提醒', note: '适合法务、采购、业务签约节点' },
				{ key: 'data', label: '数据专员', headline: '指标整理、口径核对与数据质检补位', note: '适合数据运营、BI、业务分析场景' }
			],
			agents: [
				{
					title: '财务数字员工',
					track: 'Finance',
					category: 'finance',
					badge: '热门',
					icon: 'fa fa-calculator',
					desc: '围绕月结、成本、利润与现金流，自动生成经营摘要并跟催异常。',
					meta: [
						{ label: '服务对象', value: '财务经理 / 总账 / 出纳' },
						{ label: '触发场景', value: '月结、回款、费用超标' },
						{ label: '默认交付', value: '日报、周报、风险提醒' }
					],
					tags: ['利润追踪', '回款预警', '经营简报']
				},
				{
					title: '预算跟踪员',
					track: 'Finance',
					category: 'finance',
					badge: '洞察',
					icon: 'fa fa-line-chart',
					desc: '把预算执行、部门差异和科目波动压缩成可追责的清单，适合经营会前准备。',
					meta: [
						{ label: '服务对象', value: '经营分析 / 部门负责人' },
						{ label: '触发场景', value: '预算滚动、成本复盘' },
						{ label: '默认交付', value: '偏差表、会前摘要' }
					],
					tags: ['预算执行', '差异分析', '会前材料']
				},
				{
					title: 'HR数字员工',
					track: 'HR',
					category: 'hr',
					badge: '推荐',
					icon: 'fa fa-users',
					desc: '把招聘推进、面试纪要、入转调离与提醒节点统一收口，减少 HR 的碎片跟进。',
					meta: [
						{ label: '服务对象', value: '招聘专员 / HRBP / 行政' },
						{ label: '触发场景', value: '招聘推进、入职办理' },
						{ label: '默认交付', value: '候选人摘要、待办看板' }
					],
					tags: ['招聘协同', '入职提醒', '组织变更']
				},
				{
					title: '组织发展助理',
					track: 'HR',
					category: 'hr',
					badge: '协同',
					icon: 'fa fa-calendar-check-o',
					desc: '跟踪绩效节奏、培训签到和人才盘点任务，让组织类工作从通知驱动变成流程驱动。',
					meta: [
						{ label: '服务对象', value: 'HRBP / 培训 / 直属主管' },
						{ label: '触发场景', value: '绩效季、培训月、盘点周' },
						{ label: '默认交付', value: '名单进度、提醒节奏' }
					],
					tags: ['绩效流程', '培训签到', '人才盘点']
				},
				{
					title: '法务数字员工',
					track: 'Legal',
					category: 'legal',
					badge: '稳健',
					icon: 'fa fa-gavel',
					desc: '对合同版本、履约节点和风险条款做首轮筛查，先把明显问题捞出来再交法务复核。',
					meta: [
						{ label: '服务对象', value: '法务 / 采购 / 销售支持' },
						{ label: '触发场景', value: '签约前、续约前、付款前' },
						{ label: '默认交付', value: '风险清单、节点提醒' }
					],
					tags: ['合同预审', '条款比对', '履约节点']
				},
				{
					title: '合规台账专员',
					track: 'Legal',
					category: 'legal',
					badge: '合规',
					icon: 'fa fa-shield',
					desc: '维护印章、证照、授权和制度台账，自动提醒到期节点并生成待处理事项。',
					meta: [
						{ label: '服务对象', value: '法务 / 行政 / 审计' },
						{ label: '触发场景', value: '证照续期、制度更新' },
						{ label: '默认交付', value: '到期日历、整改清单' }
					],
					tags: ['证照到期', '制度留痕', '授权管理']
				},
				{
					title: '数据专员',
					track: 'Data',
					category: 'data',
					badge: '效率',
					icon: 'fa fa-database',
					desc: '负责指标口径整理、跨系统数据拉齐和可视化摘要，适合补位数据运营日常工作。',
					meta: [
						{ label: '服务对象', value: '数据运营 / BI / 业务分析' },
						{ label: '触发场景', value: '日报汇总、专题复盘' },
						{ label: '默认交付', value: '指标卡、趋势摘要' }
					],
					tags: ['指标口径', '看板摘要', '数据整理']
				},
				{
					title: '数据质检专员',
					track: 'Data',
					category: 'data',
					badge: '自动',
					icon: 'fa fa-code-fork',
					desc: '识别缺失值、异常波动和字段映射错误，把问题前置到业务报表发布之前。',
					meta: [
						{ label: '服务对象', value: '数据治理 / 产品运营' },
						{ label: '触发场景', value: '同步后、发布前、异常日' },
						{ label: '默认交付', value: '质检结果、修复建议' }
					],
					tags: ['异常扫描', '字段映射', '发布前校验']
				}
			]
		};
	},
	computed: {
		filteredAgents() {
			if (this.activeCategory === 'all') {
				return this.agents;
			}
			return this.agents.filter(item => item.category === this.activeCategory);
		}
	},
	watch: {
		'$route.query.category'(value) {
			this.activeCategory = normalizeCategory(value);
		}
	},
	methods: {
		switchCategory(category) {
			this.$router.replace({
				name: 'EmployeeMarket',
				query: category === 'all' ? {} : { category }
			});
		}
	}
};
</script>
